import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Page publique /pricing
 * Affiche les 4 plans avec toggle mensuel/annuel.
 * Accessible sans authentification.
 */
const PricingPage = ({ onBack, onChoosePlan }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState('mensuel'); // 'mensuel' | 'annuel'
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    api.get('/abonnements/plans').then((data) => {
      if (data && data.plans) {
        setPlans(data.plans);
      }
      setLoading(false);
    });
  }, []);

  // Helpers d'affichage des fonctionnalités
  const formatOCR = (f) => {
    const ocr = f.ocr;
    if (!ocr) return ['❌', 'OCR indisponible'];
    if (ocr.limite === -1) return ['✅', 'OCR illimité'];
    return ['✅', `OCR ${ocr.limite}/mois`];
  };

  const formatChat = (f) => {
    const chat = f.chat;
    if (!chat) return ['❌', 'Chat IA indisponible'];
    if (chat.limite === -1) return ['✅', 'Chat IA illimité'];
    return ['✅', `Chat IA ${chat.limite}/mois`];
  };

  const formatBanque = (f) => {
    if (f.banque === 'auto') return ['✅', 'Rapprochement bancaire auto'];
    if (f.banque === 'manuel') return ['✅', 'Rapprochement bancaire manuel'];
    return ['❌', 'Pas de rapprochement bancaire'];
  };

  const formatSocial = (f) => {
    if (!f.social) return ['❌', 'Pas de paie'];
    if (f.social.salaries_max === -1) return ['✅', 'Paie illimitée + DSN'];
    return ['✅', `Paie jusqu'à ${f.social.salaries_max} salariés + DSN`];
  };

  const formatInventaire = (f) =>
    f.inventaire ? ['✅', 'Immobilisations & amortissements'] : ['❌', 'Pas de module inventaire'];

  const formatOptimisation = (f) => {
    if (f.optimisation === 'avancee') return ['✅', 'Optimisation fiscale avancée'];
    if (f.optimisation === 'base') return ['✅', 'Optimisation fiscale base'];
    return ['❌', 'Pas d' + "'" + 'optimisation fiscale'];
  };

  const formatCopilote = (f) => {
    const items = [];
    if (f.copilote_tva) items.push('TVA');
    if (f.copilote_urssaf) items.push('URSSAF');
    if (items.length === 0) return ['❌', 'Pas de copilote auto'];
    return ['✅', `Copilote auto ${items.join(' + ')}`];
  };

  const formatExports = (f) => {
    if (f.exports === 'all') return ['✅', 'Tous exports + API'];
    if (Array.isArray(f.exports)) return ['✅', `Exports ${f.exports.join(', ')}`];
    return ['❌', 'Pas d' + "'" + 'exports'];
  };

  const formatMultiClients = (f) => {
    if (f.multi_clients > 1) return ['✅', `Multi-sociétés (${f.multi_clients})`];
    return ['⚪', '1 société'];
  };

  // Liste ordonnée des features à afficher
  const featuresList = (f) => [
    formatOCR(f),
    formatChat(f),
    formatBanque(f),
    formatSocial(f),
    formatInventaire(f),
    formatOptimisation(f),
    formatCopilote(f),
    formatExports(f),
    formatMultiClients(f),
  ];

  // Met en avant le plan Pro
  const isHighlight = (planId) => planId === 'pro';

  // Prix à afficher selon cycle
  const prixPlan = (plan) => (cycle === 'annuel' ? plan.prix_annuel : plan.prix_mensuel);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#F5F4ED',
        fontFamily: 'Inter, system-ui, sans-serif',
        color: '#2D2D2D',
      }}
    >
      {/* Header */}
      <header
        style={{
          padding: '20px 32px',
          background: 'white',
          borderBottom: '1px solid #E8E7E3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#1D9E75',
              color: 'white',
              fontSize: '18px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            C
          </div>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>ComptaAI</span>
        </div>
        <button
          onClick={onBack}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: '1px solid #E8E7E3',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            color: '#2D2D2D',
          }}
        >
          ← Retour
        </button>
      </header>

      {/* Hero + toggle */}
      <div style={{ textAlign: 'center', padding: '48px 32px 24px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '600', margin: '0 0 12px' }}>
          Choisissez votre formule
        </h1>
        <p style={{ fontSize: '16px', color: '#73726C', margin: '0 0 32px' }}>
          30 jours d'essai gratuit · Sans engagement · Résiliable en 1 clic
        </p>

        {/* Toggle mensuel/annuel */}
        <div
          style={{
            display: 'inline-flex',
            background: 'white',
            border: '1px solid #E8E7E3',
            borderRadius: '10px',
            padding: '4px',
            gap: '4px',
          }}
        >
          <button
            onClick={() => setCycle('mensuel')}
            style={{
              padding: '8px 18px',
              background: cycle === 'mensuel' ? '#1D9E75' : 'transparent',
              color: cycle === 'mensuel' ? 'white' : '#2D2D2D',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Mensuel
          </button>
          <button
            onClick={() => setCycle('annuel')}
            style={{
              padding: '8px 18px',
              background: cycle === 'annuel' ? '#1D9E75' : 'transparent',
              color: cycle === 'annuel' ? 'white' : '#2D2D2D',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Annuel <span style={{ color: cycle === 'annuel' ? '#FFF8DC' : '#1D9E75', marginLeft: '4px' }}>-20%</span>
          </button>
        </div>
      </div>

      {/* Grille plans */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#73726C' }}>
          Chargement des formules...
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '20px',
            padding: '0 32px 48px',
            maxWidth: '1200px',
            margin: '0 auto',
          }}
        >
          {plans.map((plan) => {
            const highlight = isHighlight(plan.id);
            const features = featuresList(plan.fonctionnalites);
            const ctaLabel = plan.id === 'gratuit' ? 'Commencer gratuitement' : 'Essai 30 jours';
            const isSelected = selectedPlanId === plan.id;

            return (
              <div
                key={plan.id}
                style={{
                  background: 'white',
                  border: isSelected
                    ? '4px solid #1D9E75'
                    : (highlight ? '2px solid #1D9E75' : '1px solid #E8E7E3'),
                  borderRadius: '16px',
                  padding: '24px',
                  position: 'relative',
                  transform: highlight ? 'scale(1.02)' : 'none',
                  boxShadow: isSelected
                    ? '0 8px 24px rgba(29, 158, 117, 0.25)'
                    : (highlight ? '0 8px 24px rgba(29, 158, 117, 0.12)' : 'none'),
                  transition: 'all 0.2s ease',
                }}
              >
                {isSelected && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#1D9E75',
                      color: 'white',
                      padding: '4px 14px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      letterSpacing: '0.04em',
                      zIndex: 2,
                    }}
                  >
                    ✓ Sélectionné
                  </div>
                )}
                {highlight && !isSelected && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#1D9E75',
                      color: 'white',
                      padding: '4px 14px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      letterSpacing: '0.04em',
                    }}
                  >
                    ★ POPULAIRE
                  </div>
                )}

                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 4px' }}>{plan.nom}</h2>
                <p style={{ fontSize: '12px', color: '#73726C', margin: '0 0 20px', minHeight: '32px' }}>
                  {plan.description}
                </p>

                <div style={{ marginBottom: '24px' }}>
                  <span style={{ fontSize: '32px', fontWeight: '700', color: '#2D2D2D' }}>
                    {prixPlan(plan).toFixed(2).replace('.', ',')}€
                  </span>
                  <span style={{ fontSize: '13px', color: '#73726C' }}> / mois</span>
                  {cycle === 'annuel' && plan.prix_mensuel > 0 && (
                    <div style={{ fontSize: '11px', color: '#1D9E75', marginTop: '4px', fontWeight: '500' }}>
                      Économie {plan.economie_annuelle.toFixed(2).replace('.', ',')}€/an
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    if (selectedPlanId) return;
                    setSelectedPlanId(plan.id);
                    setTimeout(() => onChoosePlan(plan), 800);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: isSelected
                      ? '#1D9E75'
                      : (plan.id === 'gratuit' ? '#F5F4ED' : (highlight ? '#1D9E75' : 'white')),
                    color: isSelected
                      ? 'white'
                      : (plan.id === 'gratuit' ? '#2D2D2D' : (highlight ? 'white' : '#1D9E75')),
                    border: isSelected
                      ? 'none'
                      : (plan.id === 'gratuit' ? '1px solid #E8E7E3' : (highlight ? 'none' : '1px solid #1D9E75')),
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isSelected ? 'default' : 'pointer',
                    marginBottom: '20px',
                    opacity: selectedPlanId && !isSelected ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isSelected ? '✓ Continuer...' : ctaLabel}
                </button>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', lineHeight: '2' }}>
                  {features.map(([icon, label], i) => (
                    <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ flexShrink: 0 }}>{icon}</span>
                      <span style={{ color: '#2D2D2D' }}>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* FAQ simple */}
      <div
        style={{
          maxWidth: '800px',
          margin: '0 auto 48px',
          padding: '32px',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #E8E7E3',
        }}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px' }}>
          Questions fréquentes
        </h3>
        <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#2D2D2D' }}>
          <p><strong>• Puis-je changer de plan ?</strong> Oui, à tout moment depuis votre espace. Les proratas sont calculés automatiquement par Stripe.</p>
          <p><strong>• Que se passe-t-il après l'essai ?</strong> Sans carte bancaire saisie, vous basculez automatiquement sur le plan Gratuit.</p>
          <p><strong>• Les données sont-elles isolées ?</strong> Oui, chaque client a son propre schéma PostgreSQL chiffré dédié.</p>
          <p><strong>• Support ?</strong> Email pour le Gratuit, chat 24h pour le Pro, prioritaire pour l'Expert.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
